import { GraphQLSchema, ValueNode } from 'graphql';
import JSON5 from 'json5';
import * as jsoncParser from 'jsonc-parser';
import { camelCase, sortBy } from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React from 'react';

import { tolerantParseValue } from 'src/lib/chevrotain/tolerantParseValue';
import { createUniqueName } from 'src/lib/createUniqueName';
import { getRootTypeNames, unwrapType } from 'src/lib/graphql/graphqlHelpers';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { EditAction } from '../explorerDocs/getFieldToggle/executeEdits';
import {
  VariableShape,
  VariableShapes,
  getVariableShapes,
} from '../helpers/getVariableShapes/getVariableShapes';
import { printType } from '../helpers/graphqlHelpers';
import {
  ParseState,
  getAncestorStates,
  getDocumentDefinitions,
  getPathToOffset,
  runOnlineParser,
} from '../helpers/graphqlParseHelpers';
import { parseVariablesOrDefault } from '../helpers/jsonHelpers';
import {
  OperationPath,
  operationPathToGraphQLPath,
  parsePathToOperationPath,
} from '../helpers/operationPathHelpers';
import { JSONValue } from '../helpers/types';
import { useEditorModel } from '../hooks/useEditorModel';

/**
 * This parses the value in the given range, and then converts that ValueNode
 * into a json representation
 */
function graphQLVariableToJSON(
  operationModel: monacoEditor.editor.ITextModel,
  range: monacoEditor.Range,
): JSONValue {
  const { valueNode } = tolerantParseValue(operationModel, range);
  return valueNode ? innerGraphQLVariableToJSON(valueNode) : null;
}

function innerGraphQLVariableToJSON(valueNode: ValueNode): JSONValue {
  switch (valueNode.kind) {
    case 'BooleanValue':
    case 'EnumValue':
    case 'StringValue':
      return valueNode.value;
    case 'FloatValue':
      return parseFloat(valueNode.value);
    case 'IntValue':
      return parseInt(valueNode.value, 10);
    case 'ListValue':
      return valueNode.values.map(innerGraphQLVariableToJSON);
    case 'NullValue':
      return null;
    case 'ObjectValue':
      return Object.fromEntries(
        valueNode.fields.map((field) => [
          field.name.value,
          innerGraphQLVariableToJSON(field.value),
        ]),
      );
    default:
      return null;
  }
}

/**
 * Given a json value, this converts it into a graphql input representation
 */
function jsonToGraphQLVariable(
  json: JSONValue | undefined,
  variableShape: VariableShape | undefined,
  indent: number = 0,
): string {
  if (variableShape?.kind === 'enum' && typeof json === 'string') {
    return json;
  }
  // We don't check for being an array since all arrays also accept a single value
  if (variableShape?.kind === 'array') {
    return `[\n  ${
      Array.isArray(json)
        ? json
            .map((jsonEntry) =>
              jsonToGraphQLVariable(
                jsonEntry,
                variableShape.innerType,
                indent + 2,
              ),
            )
            .join(',\n  ')
        : jsonToGraphQLVariable(json, variableShape.innerType, indent + 2)
    }\n]`.replace(/\n/g, `\n${' '.repeat(indent)}`);
  }
  if (variableShape?.kind === 'object' && json && typeof json === 'object') {
    return `{\n  ${Object.entries(json)
      .map(
        ([name, jsonEntry]) =>
          `${name}: ${jsonToGraphQLVariable(
            jsonEntry,
            variableShape.fields[name],
            indent + 2,
          )}`,
      )
      .join(',\n  ')}\n}`.replace(/\n/g, `\n${' '.repeat(indent)}`);
  }
  return JSON5.stringify(json, {
    quote: '"',
    space: 2,
  });
}

// Exported for tests
export function extractVariables({
  operationModel,
  variablesModel,
  executeEdits,
  schema,
}: {
  operationModel: monacoEditor.editor.ITextModel;
  variablesModel: monacoEditor.editor.ITextModel;
  executeEdits: (action: EditAction) => void;
  schema: GraphQLSchema;
}) {
  const operationString = operationModel.getValue();
  let variablesString = variablesModel.getValue();
  const variablesValues = parseVariablesOrDefault(variablesString);
  const usedVariableNames = Object.keys(variablesValues);

  const operationEdits: monacoEditor.editor.IIdentifiedSingleEditOperation[] =
    [];
  const variablesEdits: jsoncParser.Edit[][] = [];
  let currentOperation: {
    variableWritePosition?: monacoEditor.Position;
    variableWrapper?: (text: string) => string;
    variablesToAdd: { name: string; type: string }[];
  } = {
    variablesToAdd: [],
  };

  runOnlineParser(operationString, (state, range) => {
    switch (state.kind) {
      case 'ShortQuery': {
        currentOperation.variableWrapper = (text) => `query (${text}) `;
        break;
      }
      case 'SelectionSet': {
        if (
          !currentOperation.variableWritePosition &&
          state.prevState?.kind &&
          ['Query', 'ShortQuery', 'Mutation', 'Subscription'].includes(
            state.prevState.kind,
          )
        ) {
          currentOperation.variableWritePosition = range.getStartPosition();
          currentOperation.variableWrapper = (text) => `(${text}) `;
        }

        break;
      }
      case 'Variable': {
        // If we see a variable defined with no value in the variables panel,
        // add it as null
        if (
          state.name &&
          state.prevState?.kind === 'VariableDefinition' &&
          !(state.name in variablesValues)
        ) {
          const edits = jsoncParser.modify(
            variablesString,
            [state.name],
            null,
            { formattingOptions: { insertSpaces: true, tabSize: 2 } },
          );
          // Update this so the subsequent writes go in the right places. These
          // will be applied in order.
          variablesString = jsoncParser.applyEdits(variablesString, edits);
          variablesEdits.push(edits);
        }
        break;
      }
      case 'VariableDefinition': {
        currentOperation.variableWritePosition = range.getEndPosition();
        currentOperation.variableWrapper = (text) => ` ${text}`;
        break;
      }
      case 'Definition': {
        const { variableWritePosition, variableWrapper, variablesToAdd } =
          currentOperation;
        if (!variableWritePosition || !variableWrapper) {
          return;
        }
        operationEdits.push({
          range: monacoEditor.Range.fromPositions(
            variableWritePosition,
            variableWritePosition,
          ),
          text: variableWrapper(
            `${variablesToAdd
              .map((variable) => `$${variable.name}: ${variable.type}`)
              .join(' ')}`,
          ),
        });
        currentOperation = {
          variablesToAdd: [],
        };
        break;
      }
      default: {
        // We are looking for an argument literal (not a variable)
        if (
          state.prevState?.kind === 'Value' &&
          state.prevState.prevState?.kind === 'Argument'
        ) {
          const valueState = state.prevState;
          const pathToArgument = operationPathToGraphQLPath(
            parsePathToOperationPath(getAncestorStates(valueState)),
            schema,
          )?.slice(-2);
          if (isMinLengthArray(2, pathToArgument)) {
            const [fieldNode, argumentNode] = pathToArgument;
            const typeNode =
              argumentNode && 'type' in argumentNode
                ? argumentNode.type
                : argumentNode;

            const value: JSONValue = graphQLVariableToJSON(
              operationModel,
              range,
            );
            const variableName = createUniqueName(
              `${fieldNode.name} ${argumentNode.name}`,
              usedVariableNames,
              camelCase,
            );
            usedVariableNames.push(variableName);
            currentOperation.variablesToAdd.push({
              name: variableName,
              type: printType(typeNode),
            });

            const edits = jsoncParser.modify(
              variablesString,
              [variableName],
              value,
              { formattingOptions: { insertSpaces: true, tabSize: 2 } },
            );
            // Update this so the subsequent writes go in the right places. These
            // will be applied in order.
            variablesString = jsoncParser.applyEdits(variablesString, edits);
            variablesEdits.push(edits);

            operationEdits.push({
              range,
              text: `$${variableName}`,
            });
          }
        }
        break;
      }
    }
  });

  executeEdits({
    type: 'inlineVariables',
    edits: operationEdits,
    variableEdits: variablesEdits,
  });
}

// Exported for tests
export function inlineVariables({
  operationModel,
  variablesModel,
  schema,
  executeEdits,
}: {
  operationModel: monacoEditor.editor.ITextModel;
  variablesModel: monacoEditor.editor.ITextModel;
  schema: GraphQLSchema;
  executeEdits: (action: EditAction) => void;
}) {
  const operationString = operationModel.getValue();
  let variablesString = variablesModel.getValue();
  const variablesValues = parseVariablesOrDefault(variablesString);

  const variableShapes: VariableShapes | { fields: undefined } = Object.assign(
    {},
    ...(getVariableShapes(schema, operationString) ?? []),
  );

  const removedVariables: Set<string> = new Set([]);
  const operationEdits: monacoEditor.editor.IIdentifiedSingleEditOperation[] =
    [];
  const variablesEdits: jsoncParser.Edit[][] = [];
  runOnlineParser(operationString, (state, range) => {
    switch (state.kind) {
      case 'VariableDefinitions': {
        operationEdits.push({ range, text: '' });
        break;
      }
      case 'Variable': {
        if (!state.name) {
          return;
        }
        if (
          state.prevState?.kind === 'VariableDefinition' &&
          !removedVariables.has(state.name)
        ) {
          if (state.name in variablesValues) {
            const edits = jsoncParser.modify(
              variablesString,
              [state.name],
              undefined,
              { formattingOptions: { insertSpaces: true, tabSize: 2 } },
            );
            variablesEdits.push(edits);
            // Update this so the subsequent writes go in the right places. These
            // will be applied in order.
            variablesString = jsoncParser.applyEdits(variablesString, edits);
          }
          removedVariables.add(state.name);
        }
        if (state.prevState?.kind === 'Value') {
          const indent =
            operationModel
              .getLineContent(range.startLineNumber)
              .match(/^ +/)?.[0]?.length ?? 0;
          operationEdits.push({
            range,
            text: jsonToGraphQLVariable(
              variablesValues[state.name] ?? null,
              variableShapes.fields?.[state.name],
            ).replace(/\n/g, `\n${' '.repeat(indent)}`),
          });
        }
        break;
      }
      default: {
        // Ignore default
      }
    }
  });

  executeEdits({
    type: 'inlineVariables',
    edits: operationEdits,
    variableEdits: variablesEdits,
  });
}

interface SelectionInfo {
  state: ParseState;
  range: monacoEditor.Range;
  depth: number;
}
export function getExtractionDetails({
  operationModel,
  operationSelection,
  schema,
}: {
  operationModel: monacoEditor.editor.ITextModel;
  operationSelection: monacoEditor.Selection;
  schema: GraphQLSchema;
}) {
  const operationString = operationModel.getValue();

  // Get selected fields for current position/selection
  const selections: SelectionInfo[] = [];
  const parents: SelectionInfo[] = [];
  runOnlineParser(operationString, (state, range) => {
    switch (state.kind) {
      case 'AliasedField':
      case 'InlineFragment':
      case 'FragmentSpread':
      case 'Field': {
        if (operationSelection.intersectRanges(range)) {
          if (!range.strictContainsRange(operationSelection)) {
            selections.push({
              state,
              range,
              depth: getAncestorStates(state).length,
            });
          } else {
            parents.push({
              state,
              range,
              depth: getAncestorStates(state).length,
            });
          }
        }
        break;
      }
      default: {
        // Ignore default
      }
    }
  });

  // Get the outermost fields inside the selection, or the innermost containing
  // field.
  let selectionsToExtract: SelectionInfo[];
  if (selections.length) {
    const minDepth = Math.min(...selections.map(({ depth }) => depth));
    selectionsToExtract = selections.filter(({ depth }) => depth === minDepth);
  } else {
    const maxDepth = Math.max(...parents.map(({ depth }) => depth));
    selectionsToExtract = parents.filter(({ depth }) => depth === maxDepth);
  }
  selectionsToExtract = sortBy(selectionsToExtract, (field) =>
    operationModel.getOffsetAt(field.range.getStartPosition()),
  );
  if (!isMinLengthArray(1, selectionsToExtract)) return;

  // Get the type for the enclosing field
  // Fragment spreads are not included in Operation Paths
  const isFragmentSpread =
    selectionsToExtract[0].state.kind === 'FragmentSpread';
  const parentNode = operationPathToGraphQLPath(
    parsePathToOperationPath(getAncestorStates(selectionsToExtract[0].state)),
    schema,
  )?.slice(isFragmentSpread ? -1 : -2)?.[0];
  if (!parentNode) return;
  const parentTypeName = unwrapType(parentNode).name;

  return { parentTypeName, selectionsToExtract };
}

export function wrapWithDeferFragment({
  operationEditor,
  executeEdits,
  schema,
}: {
  operationEditor: monacoEditor.editor.IStandaloneCodeEditor;
  executeEdits: (action: EditAction) => void;
  schema: GraphQLSchema;
}) {
  const operationModel = operationEditor.getModel();
  const operationSelection = operationEditor.getSelection();
  if (!operationModel || !operationSelection) return;

  const extractionDetails = getExtractionDetails({
    operationModel,
    operationSelection,
    schema,
  });
  if (!extractionDetails) return;
  const { selectionsToExtract } = extractionDetails;

  const range = monacoEditor.Range.fromPositions(
    selectionsToExtract[0].range.getStartPosition(),
    selectionsToExtract.slice(-1)[0]?.range.getEndPosition(),
  );

  const indentation = operationModel
    .getLineContent(range.getStartPosition().lineNumber)
    .substring(0, range.getStartPosition().column - 1);

  const wrappedContentIndented = operationModel
    .getValueInRange(range)
    .split('\n')
    .map((line) => `  ${line}`)
    .join('\n');

  executeEdits({
    type: 'wrapWithDeferFragment',
    edits: [
      {
        range,
        text: `... @defer {\n${indentation}${wrappedContentIndented}\n${indentation}}`,
      },
    ],
    variableEdits: [],
  });
}

export function extractFragment({
  operationEditor,
  executeEdits,
  schema,
  withDirective,
}: {
  operationEditor: monacoEditor.editor.IStandaloneCodeEditor;
  executeEdits: (action: EditAction) => void;
  schema: GraphQLSchema;
  withDirective?: string;
}) {
  const operationModel = operationEditor.getModel();
  const operationSelection = operationEditor.getSelection();
  if (!operationModel || !operationSelection) return;

  const extractionDetails = getExtractionDetails({
    operationModel,
    operationSelection,
    schema,
  });
  if (!extractionDetails) return;
  const { parentTypeName, selectionsToExtract } = extractionDetails;

  const range = monacoEditor.Range.fromPositions(
    selectionsToExtract[0].range.getStartPosition(),
    selectionsToExtract.slice(-1)[0]?.range.getEndPosition(),
  );

  const { fragmentDefinitions } = getDocumentDefinitions(
    operationModel.getValue(),
    getRootTypeNames(schema),
  );
  const fragmentNames = new Set(fragmentDefinitions.map(({ name }) => name));
  const fragmentNumber = [...Array(fragmentNames.size + 1)].findIndex(
    (_, i) => !fragmentNames.has(`${parentTypeName}Fragment${i || ''}`),
  );
  const fragmentName = `${parentTypeName}Fragment${fragmentNumber || ''}`;

  const fragmentContentIndentation = operationModel
    .getLineContent(range.getStartPosition().lineNumber)
    .substring(0, range.getStartPosition().column - 1);

  const wrappedContentIndented = operationModel
    .getValueInRange(range)
    .split('\n')
    // in a perfectly formatted gql operation, this shifts everything over so the
    // first and last line are 2 spaces indented. However, we don't know if
    // the content is formatted, so if we try to delete non-whitespace, bail
    .map((line) =>
      line.substring(0, fragmentContentIndentation.length).trim().length === 0
        ? `  ${line.substring(fragmentContentIndentation.length)}`
        : line,
    )
    .join('\n');

  executeEdits({
    type: 'extractFragment',
    edits: [
      {
        range,
        text: `...${fragmentName}${withDirective ? ` ${withDirective}` : ''}`,
      },
      {
        range: new monacoEditor.Range(1, 1, 1, 1),
        text: `fragment ${fragmentName} on ${parentTypeName} {\n  ${wrappedContentIndented}\n}\n\n`,
      },
    ],
    variableEdits: [],
  });
}

export function canExtractFragment({
  operationEditor,
  schema,
}: {
  operationEditor: monacoEditor.editor.IStandaloneCodeEditor;
  schema: GraphQLSchema;
}) {
  const operationModel = operationEditor.getModel();
  const operationSelection = operationEditor.getSelection();
  if (!operationModel || !operationSelection) return false;
  return !!getExtractionDetails({
    operationModel,
    operationSelection,
    schema,
  });
}

export function getInlineDetails({
  operationModel,
  operationPosition,
  schema,
}: {
  operationModel: monacoEditor.editor.ITextModel;
  operationPosition: monacoEditor.Position;
  schema: GraphQLSchema;
}) {
  let fragmentSpreadInfo:
    | {
        name: string;
        spreads: {
          operationPath: OperationPath;
          range: monacoEditor.Range;
        }[];
      }
    | undefined;

  const path = getPathToOffset(operationModel, operationPosition);
  if (
    path?.path?.length &&
    path.endState.name &&
    path.endState.kind === 'FragmentSpread'
  ) {
    fragmentSpreadInfo = {
      name: path.endState.name,
      spreads: [
        {
          operationPath: path.path,
          range: path.endRange,
        },
      ],
    };
  }
  if (!fragmentSpreadInfo) {
    let selectedFragment: { name: string | null } | undefined;
    const fragmentSpreadsByName: Record<
      string,
      { state: ParseState; range: monacoEditor.Range }[]
    > = {};
    runOnlineParser(operationModel.getValue(), (state, range) => {
      if (
        range.containsPosition(operationPosition) &&
        selectedFragment === undefined
      ) {
        // If the first range to contain the position is a fragment definition,
        // inline all matching fragment spreads.
        if (state.name && state.kind === 'FragmentDefinition') {
          selectedFragment = { name: state.name };
        } else {
          selectedFragment = { name: null };
        }
      }
      if (state.name && state.kind === 'FragmentSpread') {
        fragmentSpreadsByName[state.name] = (
          fragmentSpreadsByName[state.name] ?? []
        ).concat({ state, range });
      }
    });
    if (selectedFragment?.name) {
      fragmentSpreadInfo = {
        name: selectedFragment.name,
        spreads:
          fragmentSpreadsByName[selectedFragment.name]?.map((spread) => ({
            operationPath: parsePathToOperationPath(
              getAncestorStates(spread.state),
            ),
            range: spread.range,
          })) ?? [],
      };
    }
  }
  if (!fragmentSpreadInfo) return;
  const { name: fragmentName, spreads: fragmentSpreads } = fragmentSpreadInfo;

  const filteredFragmentSpreads = fragmentSpreads?.flatMap(
    ({ operationPath, range }) => {
      const graphqlPath = operationPathToGraphQLPath(operationPath, schema);
      const graphqlNode = graphqlPath?.slice(-1)?.[0];
      if (graphqlNode) {
        const parentTypeName = unwrapType(graphqlNode).name;

        const fragmentSpreadString = operationModel.getValueInRange(range);
        const contentAfterTypeDef = fragmentSpreadString.split(fragmentName)[1];
        return { parentTypeName, range, contentAfterTypeDef };
      }
      return [];
    },
  );
  if (!filteredFragmentSpreads.length) return;

  const { fragmentDefinitions } = getDocumentDefinitions(
    operationModel.getValue(),
    getRootTypeNames(schema),
  );
  const fragment = fragmentDefinitions.find(
    (definition) => definition.name === fragmentName,
  );
  if (!fragment) return;

  return { fragment, spreads: filteredFragmentSpreads };
}
export function inlineFragment({
  operationEditor,
  executeEdits,
  schema,
}: {
  operationEditor: monacoEditor.editor.IStandaloneCodeEditor;
  executeEdits: (action: EditAction) => void;
  schema: GraphQLSchema;
}) {
  const operationModel = operationEditor.getModel();
  const operationPosition = operationEditor.getPosition();
  if (!operationModel || !operationPosition) return;

  const inlineDetails = getInlineDetails({
    operationModel,
    operationPosition,
    schema,
  });

  if (!inlineDetails) return;
  const { fragment, spreads } = inlineDetails;

  const spreadsWithDirectiveInfo = spreads.map((spread) => ({
    ...spread,
    withDirective: spread.contentAfterTypeDef,
  }));

  const operationString = operationModel.getValue();
  let fragmentUsageCount = 0;
  runOnlineParser(operationString, (state) => {
    if (state.kind === 'FragmentSpread' && state.name === fragment.name) {
      fragmentUsageCount += 1;
    }
  });

  const fragmentText = operationModel.getValueInRange(fragment.range);
  const fragmentInnerText = fragmentText
    .slice(fragmentText.indexOf('{') + 1, fragmentText.lastIndexOf('}'))
    .trim();

  executeEdits({
    type: 'inlineFragment',
    edits: [
      {
        range:
          fragmentUsageCount > spreads.length
            ? new monacoEditor.Range(1, 1, 1, 1)
            : fragment.range,
        text: '',
      },
      ...spreadsWithDirectiveInfo.map((spread) => {
        const needsSpecifiedType = fragment.type !== spread.parentTypeName;
        return {
          range: spread.range,
          text: needsSpecifiedType
            ? `... on ${fragment.type} { ${
                spread.withDirective
                  ? `... ${spread.withDirective} { ${fragmentInnerText} }`
                  : fragmentInnerText
              }`
            : spread.withDirective
              ? `... ${spread.withDirective} { ${fragmentInnerText} }`
              : fragmentInnerText,
        };
      }),
    ],
    variableEdits: [],
  });
}
export function canInlineFragment({
  operationEditor,
  schema,
}: {
  operationEditor: monacoEditor.editor.IStandaloneCodeEditor;
  schema: GraphQLSchema;
}) {
  const operationModel = operationEditor.getModel();
  const operationPosition = operationEditor.getPosition();
  if (!operationModel || !operationPosition) return false;
  return !!getInlineDetails({
    operationModel,
    operationPosition,
    schema,
  });
}

export interface EditorRefactors {
  extractVariables: () => void;
  inlineVariables: () => void;
  extractFragment: ({ isDeferFragment }: { isDeferFragment: boolean }) => void;
  wrapWithDeferFragment: () => void;
  canExtractFragment: () => boolean;
  inlineFragment: () => void;
  canInlineFragment: () => boolean;
}

// Switch this to use an editor instead of a model
export function useEditorRefactorsRef({
  operationEditor,
  variablesEditor,
  executeEdits,
  schema,
}: {
  operationEditor: monacoEditor.editor.IStandaloneCodeEditor;
  variablesEditor: monacoEditor.editor.IStandaloneCodeEditor;
  executeEdits: ((action: EditAction) => void) | null;
  schema: GraphQLSchema;
}) {
  const operationModel = useEditorModel(operationEditor);
  const variablesModel = useEditorModel(variablesEditor);
  const editorRefactors = React.useMemo((): EditorRefactors | null => {
    if (!operationModel || !variablesModel || !executeEdits) {
      return null;
    }
    return {
      extractVariables: () =>
        extractVariables({
          operationModel,
          variablesModel,
          executeEdits,
          schema,
        }),
      inlineVariables: () =>
        inlineVariables({
          operationModel,
          variablesModel,
          schema,
          executeEdits,
        }),
      extractFragment: ({ isDeferFragment }: { isDeferFragment: boolean }) =>
        extractFragment({
          operationEditor,
          executeEdits,
          schema,
          withDirective: isDeferFragment ? '@defer' : undefined,
        }),
      canExtractFragment: () => canExtractFragment({ operationEditor, schema }),
      wrapWithDeferFragment: () =>
        wrapWithDeferFragment({ operationEditor, executeEdits, schema }),
      inlineFragment: () =>
        inlineFragment({
          operationEditor,
          executeEdits,
          schema,
        }),
      canInlineFragment: () => canInlineFragment({ operationEditor, schema }),
    };
  }, [operationEditor, operationModel, variablesModel, executeEdits, schema]);

  const editorRefactorsRef = React.useRef(editorRefactors);
  editorRefactorsRef.current = editorRefactors;
  return editorRefactorsRef;
}
